/**
 * The workflow canvas's geometry, ported from AmoregGTM's campaign canvas
 * (features/workflow/geometry.ts and components/flow/geometry.ts): steps
 * flow top to bottom, links are right-angled routes with 8px rounded
 * corners from a step's bottom centre to the next step's top centre, an
 * open outlet is a stub with an endpoint, and the view pans and zooms
 * (25% to 200%) about the canvas's horizontal centre.
 *
 * Eve's outlets: one way on ("out"), a Condition's Yes and No, and none on
 * Output. Unlike a campaign, a step may have several links out of one
 * outlet (they run side by side); an outlet's stub shows only while it has
 * none.
 *
 * @module components/workflows/canvas/geometry
 */
import type { WorkflowEdge, WorkflowGraph, WorkflowNode, WorkflowNodeType } from "@amoreg/contracts";
import { workflowStepBranches } from "@repo/shared/workflowGraph";
import { WORKFLOW_CARD } from "@repo/shared/workflowLayout";

export interface Point {
  readonly x: number;
  readonly y: number;
}
/** `x` is measured from the canvas's horizontal centre, `y` from its top. */
export interface View {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}
export type PortId = "out" | "yes" | "no";

export const NODE_WIDTH = WORKFLOW_CARD.width;
export const NODE_HEIGHT = WORKFLOW_CARD.height;
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2;
export const NODE_GAP = WORKFLOW_CARD.gap;
/** The dot grid's base spacing, as GTM draws it. */
export const GRID_SIZE = 10.86;

const BRANCH_SPREAD = 200;
const BRANCH_END = 87;
const OUT_END = 56;
const ELBOW = 40;
const CORNER = 8;
const STRAIGHT = 28;
const STUB_CLEARANCE = 28;

export const DEFAULT_VIEW: View = { x: 0, y: 48, zoom: 1 };

type Placed = Pick<WorkflowNode, "id" | "type" | "position">;

export const nodeHeight = (node: Pick<WorkflowNode, "id">, sizes: Readonly<Record<string, number>>) => sizes[node.id] ?? NODE_HEIGHT;

/**
 * The canvas's own rectangle less what floats over its edges (the Steps
 * panel on the left, an open step's panel on the right). Whatever sits beside
 * the canvas, such as the Ask eve panel, is already outside `width`.
 */
export interface CanvasArea {
  readonly width: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
}

const REVEAL_MARGIN = 16;
const STEP_PANEL_WIDTH = 480;

/**
 * How much of the canvas's right edge an open step's panel takes: it is
 * `min(480px, 100% - 32px)` wide and 16px in from the edge, and the chart
 * keeps a 16px gap from it.
 */
export const stepPanelInset = (width: number): number =>
  Math.max(0, Math.min(STEP_PANEL_WIDTH, width - 2 * REVEAL_MARGIN)) + 2 * REVEAL_MARGIN;

const STEPS_PANEL = { width: 320, header: 52 } as const;
export const NOTICE_MIN_ROW = 320;
const NOTICE_HEIGHT = 36;

export interface NoticeSlot {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/**
 * Where notices over the chart go: the top row between the Steps panel and
 * an open step's panel, else below the folded Steps header; null when the
 * panels leave no row, as the person's own panels then fill the chart.
 */
export function noticeSlot(input: { readonly width: number; readonly steps: "none" | "folded" | "open"; readonly stepPanel: boolean }): NoticeSlot | null {
  const right = input.stepPanel ? stepPanelInset(input.width) : REVEAL_MARGIN;
  const fits = (left: number) => input.width - left - right >= NOTICE_MIN_ROW;
  const at = (left: number, top: number): NoticeSlot => ({ left, right, top, bottom: top + NOTICE_HEIGHT + REVEAL_MARGIN / 4 });
  const beside = input.steps === "none" ? REVEAL_MARGIN : Math.min(STEPS_PANEL.width, input.width - 2 * REVEAL_MARGIN) + 2 * REVEAL_MARGIN;
  if (fits(beside)) return at(beside, REVEAL_MARGIN);
  if (input.steps === "folded" && fits(REVEAL_MARGIN)) return at(REVEAL_MARGIN, REVEAL_MARGIN + STEPS_PANEL.header + REVEAL_MARGIN / 2);
  return null;
}

/** Whether a step sits wholly inside the canvas's open area. */
export function stepInView(node: Pick<WorkflowNode, "position">, height: number, view: View, area: CanvasArea): boolean {
  const left = area.width / 2 + view.x + node.position.x * view.zoom;
  const top = view.y + node.position.y * view.zoom;
  return (
    left >= area.left &&
    left + NODE_WIDTH * view.zoom <= area.width - area.right &&
    top >= REVEAL_MARGIN &&
    top + height * view.zoom <= area.height - REVEAL_MARGIN
  );
}

/**
 * The view offset that centres a step in the canvas's open area at `zoom`.
 * A step wider than that area starts at its left edge, so its title shows
 * beside whatever floats on the right.
 */
export function viewCentredOn(node: Pick<WorkflowNode, "position">, height: number, zoom: number, area: CanvasArea): Point {
  const open = area.width - area.left - area.right;
  const stepWidth = NODE_WIDTH * zoom;
  const stepLeft = area.left + Math.max(0, (open - stepWidth) / 2);
  return {
    x: stepLeft - area.width / 2 - node.position.x * zoom,
    y: area.height / 2 - (node.position.y + height / 2) * zoom,
  };
}

/** A step's outlets: a Condition's or a Compare step's Yes and No, none on Output, else one. */
export function portsOf(type: WorkflowNodeType): ReadonlyArray<PortId> {
  if (type === "output") return [];
  return workflowStepBranches(type) ? ["yes", "no"] : ["out"];
}

export const hasInput = (type: WorkflowNodeType) => type !== "start";

/** The outlet a link leaves by. */
export const portOfEdge = (edge: Pick<WorkflowEdge, "sourceHandle">): PortId =>
  edge.sourceHandle === "yes" || edge.sourceHandle === "no" ? edge.sourceHandle : "out";

/** The `sourceHandle` a link from an outlet carries (none for the single outlet). */
export const handleOfPort = (port: PortId): "yes" | "no" | null => (port === "out" ? null : port);

export const inputPoint = (node: Placed): Point => ({ x: node.position.x + NODE_WIDTH / 2, y: node.position.y });
export const outputPoint = (node: Placed, height: number): Point => ({ x: node.position.x + NODE_WIDTH / 2, y: node.position.y + height });

/** Where an open outlet's stub would end with nothing below it: Yes down and left, No down and right, one way on straight down. */
export function endpointPoint(node: Placed, height: number, port: PortId): Point {
  const origin = outputPoint(node, height);
  if (port === "yes") return { x: origin.x - BRANCH_SPREAD, y: origin.y + BRANCH_END };
  if (port === "no") return { x: origin.x + BRANCH_SPREAD, y: origin.y + BRANCH_END };
  return { x: origin.x, y: origin.y + OUT_END };
}

export interface OpenPort {
  readonly source: Placed;
  readonly port: PortId;
  readonly point: Point;
}

/** The outlets no link leaves by yet, with where their stub ends. */
export function openPorts(graph: Pick<WorkflowGraph, "nodes" | "edges">, sizes: Readonly<Record<string, number>>): ReadonlyArray<OpenPort> {
  return graph.nodes.flatMap((node) =>
    portsOf(node.type)
      .filter((port) => !graph.edges.some((edge) => edge.source === node.id && portOfEdge(edge) === port))
      .map((port) => ({ source: node, port, point: stopShortOfCardBelow(node, endpointPoint(node, nodeHeight(node, sizes), port), graph.nodes, sizes) })),
  );
}

function stopShortOfCardBelow(source: Placed, point: Point, nodes: ReadonlyArray<Placed>, sizes: Readonly<Record<string, number>>): Point {
  const bottom = outputPoint(source, nodeHeight(source, sizes)).y;
  let top = Number.POSITIVE_INFINITY;
  for (const node of nodes) {
    const below = node.position.y > bottom && node.position.y - STUB_CLEARANCE <= point.y;
    const across = point.x >= node.position.x && point.x <= node.position.x + NODE_WIDTH;
    if (node.id !== source.id && below && across) top = Math.min(top, node.position.y);
  }
  if (top === Number.POSITIVE_INFINITY) return point;
  return { x: point.x, y: Math.max(bottom + CORNER * 3, top - STUB_CLEARANCE) };
}

/** Where a step added at an outlet goes: below it, Yes to the left, No to the right. */
export function placementFor(source: Placed, port: PortId, sizes: Readonly<Record<string, number>>): Point {
  const origin = outputPoint(source, nodeHeight(source, sizes));
  const offset = port === "yes" ? -(BRANCH_SPREAD + 20) : port === "no" ? BRANCH_SPREAD + 20 : 0;
  const drop = port === "out" ? NODE_GAP : NODE_GAP + ELBOW;
  return { x: origin.x + offset - NODE_WIDTH / 2, y: origin.y + drop };
}

/** The nearest place at or right of `point` that covers no step. */
export function freePlacement(point: Point, nodes: ReadonlyArray<Placed>, sizes: Readonly<Record<string, number>>): Point {
  const overlaps = (candidate: Point) =>
    nodes.some(
      (node) =>
        candidate.x < node.position.x + NODE_WIDTH + 24 &&
        candidate.x + NODE_WIDTH + 24 > node.position.x &&
        candidate.y < node.position.y + nodeHeight(node, sizes) + 24 &&
        candidate.y + NODE_HEIGHT + 24 > node.position.y,
    );
  // Steps are finite, so moving right always reaches a free place.
  let candidate = point;
  while (overlaps(candidate)) candidate = { x: candidate.x + NODE_WIDTH + 40, y: candidate.y };
  return candidate;
}

/** A link's corners: straight down when lined up, else down, across and down; a target above loops round the side. */
export function routePoints(source: Point, target: Point): Point[] {
  const drop = target.y - source.y;
  if (drop >= CORNER * 3) {
    if (Math.abs(target.x - source.x) < STRAIGHT) return [source, target];
    const elbow = source.y + Math.min(ELBOW, drop / 2);
    return [source, { x: source.x, y: elbow }, { x: target.x, y: elbow }, target];
  }
  const lane = Math.max(source.x, target.x) + NODE_WIDTH / 2 + ELBOW;
  const below = source.y + CORNER * 3;
  const above = target.y - CORNER * 3;
  return [source, { x: source.x, y: below }, { x: lane, y: below }, { x: lane, y: above }, { x: target.x, y: above }, target];
}

/** Like `routePoints`, but several links into one step share its elbow just above it. */
export function routeInto(source: Point, target: Point): Point[] {
  const drop = target.y - source.y;
  if (drop >= CORNER * 3 && Math.abs(target.x - source.x) >= STRAIGHT && drop > ELBOW * 2) {
    const elbow = target.y - ELBOW;
    return [source, { x: source.x, y: elbow }, { x: target.x, y: elbow }, target];
  }
  return routePoints(source, target);
}

/** An SVG path through the corners, each rounded to at most 8px. */
export function roundedPath(points: ReadonlyArray<Point>): string {
  const [first, ...rest] = points;
  if (!first) return "";
  let path = `M${first.x} ${first.y}`;
  for (let index = 0; index < rest.length; index++) {
    const corner = rest[index]!;
    const next = rest[index + 1];
    if (!next) {
      path += ` L${corner.x} ${corner.y}`;
      break;
    }
    const previous = points[index]!;
    const inLength = Math.hypot(corner.x - previous.x, corner.y - previous.y);
    const outLength = Math.hypot(next.x - corner.x, next.y - corner.y);
    const radius = Math.min(CORNER, inLength / 2, outLength / 2);
    if (radius < 0.5) {
      path += ` L${corner.x} ${corner.y}`;
      continue;
    }
    const inX = (corner.x - previous.x) / inLength;
    const inY = (corner.y - previous.y) / inLength;
    const outX = (next.x - corner.x) / outLength;
    const outY = (next.y - corner.y) / outLength;
    const sweep = inX * outY - inY * outX > 0 ? 1 : 0;
    path += ` L${corner.x - inX * radius} ${corner.y - inY * radius}`;
    path += ` A${radius} ${radius} 0 0 ${sweep} ${corner.x + outX * radius} ${corner.y + outY * radius}`;
  }
  return path;
}

/** Where a link's label sits: on its horizontal run, else its middle. */
export function labelPoint(points: ReadonlyArray<Point>): Point {
  if (points.length === 4) return { x: points[1]!.x + (points[2]!.x - points[1]!.x) * 0.53, y: points[1]!.y };
  if (points.length === 2) return { x: (points[0]!.x + points[1]!.x) / 2, y: (points[0]!.y + points[1]!.y) / 2 };
  const middle = Math.floor((points.length - 1) / 2);
  return { x: (points[middle]!.x + points[middle + 1]!.x) / 2, y: (points[middle]!.y + points[middle + 1]!.y) / 2 };
}

/** Where a selected link's Delete sits: halfway down its last run. */
export function actionPoint(points: ReadonlyArray<Point>): Point {
  const last = points.at(-1)!;
  const beforeLast = points.at(-2)!;
  return { x: last.x, y: (last.y + beforeLast.y) / 2 };
}

/** How far apart links between the same two steps run. */
const PARALLEL_GAP = 28;

export interface RoutedEdge {
  readonly edge: WorkflowEdge;
  readonly source: WorkflowNode;
  readonly target: WorkflowNode;
  readonly from: Point;
  readonly to: Point;
  readonly points: ReadonlyArray<Point>;
  readonly path: string;
}

/** Every link's route as it is drawn; a link whose step is missing is left out. */
export function routeEdges(graph: Pick<WorkflowGraph, "nodes" | "edges">, sizes: Readonly<Record<string, number>>): ReadonlyArray<RoutedEdge> {
  const byId = new Map<string, WorkflowNode>(graph.nodes.map((node) => [node.id, node]));
  const inbound = new Map<string, number>();
  for (const edge of graph.edges) inbound.set(edge.target, (inbound.get(edge.target) ?? 0) + 1);
  // Links between the same two steps (a Condition's Yes and No into one step) run side by side, each its own to select.
  const parallel = new Map<string, string[]>();
  for (const edge of graph.edges) {
    const key = `${edge.source}>${edge.target}`;
    parallel.set(key, [...(parallel.get(key) ?? []), edge.id]);
  }
  return graph.edges.flatMap((edge) => {
    const source = byId.get(edge.source);
    const target = byId.get(edge.target);
    if (!source || !target) return [];
    const siblings = parallel.get(`${edge.source}>${edge.target}`) ?? [edge.id];
    const offset = (siblings.indexOf(edge.id) - (siblings.length - 1) / 2) * PARALLEL_GAP;
    const out = outputPoint(source, nodeHeight(source, sizes));
    const into = inputPoint(target);
    const from = { x: out.x + offset, y: out.y };
    const to = { x: into.x + offset, y: into.y };
    // Several links into one step share its elbow just above it.
    const points = (inbound.get(target.id) ?? 0) > 1 ? routeInto(from, to) : routePoints(from, to);
    return [{ edge, source, target, from, to, points, path: roundedPath(points) }];
  });
}

type SnapBox = { x: number; y: number; width: number; height: number };

function alignAxis(moving: SnapBox, others: ReadonlyArray<SnapBox>, tolerance: number, axis: "x" | "y"): number {
  const center = axis === "x" ? moving.x + moving.width / 2 : moving.y + moving.height / 2;
  let best = 0;
  let closest = tolerance;
  for (const other of others) {
    const otherCenter = axis === "x" ? other.x + other.width / 2 : other.y + other.height / 2;
    const delta = otherCenter - center;
    if (Math.abs(delta) > closest) continue;
    closest = Math.abs(delta);
    best = delta;
  }
  return best;
}

/** A dragged step's place, its centre snapped to another step's when within `tolerance`. */
export function snapToNodes(moving: SnapBox, others: ReadonlyArray<SnapBox>, tolerance: number): Point {
  return { x: moving.x + alignAxis(moving, others, tolerance, "x"), y: moving.y + alignAxis(moving, others, tolerance, "y") };
}

export function toWorld(clientX: number, clientY: number, rect: Pick<DOMRect, "left" | "top" | "width">, view: View): Point {
  return { x: (clientX - rect.left - rect.width / 2 - view.x) / view.zoom, y: (clientY - rect.top - view.y) / view.zoom };
}

export const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/**
 * The most one wheel event may move the zoom, in wheel pixels: a mouse
 * wheel's notch reports about 100 (Firefox: 3 lines), which would otherwise
 * zoom by almost three times per notch, while a trackpad pinch reports a
 * stream of small deltas that stay under the cap and track the fingers.
 */
const WHEEL_ZOOM_STEP = 10;

/** The factor one Ctrl/pinch wheel event zooms by: about 10% per mouse-wheel notch. */
export function wheelZoomFactor(deltaY: number, deltaMode: number): number {
  const pixels = deltaY * (deltaMode === 1 ? 16 : deltaMode === 2 ? 800 : 1);
  return Math.exp(-Math.max(-WHEEL_ZOOM_STEP, Math.min(WHEEL_ZOOM_STEP, pixels)) * 0.01);
}

/** The view after zooming to `zoom` about a point of the canvas (`localX`, `localY` from its top left). */
export function zoomAround(view: View, zoom: number, localX: number, localY: number, width: number): View {
  const next = clampZoom(zoom);
  const worldX = (localX - width / 2 - view.x) / view.zoom;
  const worldY = (localY - view.y) / view.zoom;
  return { zoom: next, x: localX - width / 2 - worldX * next, y: localY - worldY * next };
}

/**
 * The view that shows every step, never above 100% nor below `minZoom` for
 * its height: a chart taller than the room starts at its top instead. A
 * chart wider than the room still fits its width (GTM's flow fit).
 */
export function fitView(
  nodes: ReadonlyArray<Placed>,
  sizes: Readonly<Record<string, number>>,
  room: { readonly width: number; readonly height: number; readonly left?: number; readonly right?: number; readonly top?: number },
  minZoom = MIN_ZOOM,
): View | null {
  if (nodes.length === 0 || room.width === 0 || room.height === 0) return null;
  const left = room.left ?? 0;
  const right = room.right ?? 0;
  const top = room.top ?? 0;
  const minX = Math.min(...nodes.map((node) => node.position.x)) - 40;
  const maxX = Math.max(...nodes.map((node) => node.position.x + NODE_WIDTH)) + 40 + 180;
  const minY = Math.min(...nodes.map((node) => node.position.y)) - 24;
  const maxY = Math.max(...nodes.map((node) => node.position.y + nodeHeight(node, sizes))) + 104;
  const across = (room.width - left - right - 64) / (maxX - minX);
  const raw = Math.min(across, (room.height - top - 64) / (maxY - minY));
  const zoom = clampZoom(Math.min(1, across, Math.max(raw, minZoom)));
  const centerX = left + (room.width - left - right) / 2;
  return {
    zoom,
    x: centerX - room.width / 2 - ((minX + maxX) / 2) * zoom,
    y: raw >= zoom ? top + (room.height - top) / 2 - ((minY + maxY) / 2) * zoom : top + 76 - minY * zoom,
  };
}
